import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, List, MoreVertical, Truck, ChevronLeft, ChevronRight, RotateCcw, Eye, Banknote, CheckCircle2, PackageX, AlertTriangle, Flag, Clock, DollarSign } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import OrdenAccionesDialog from "./OrdenAccionesDialog";
import { RecepcionDetalleDialog } from "./RecepcionDetalleDialog";
import { useState, useMemo, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { LiveIndicator } from "@/components/ui/live-indicator";
import { useIsMobile } from "@/hooks/use-mobile";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isHoliday, getMexicanHolidays, MexicanHoliday } from "@/lib/mexicanHolidays";

// Type for per-delivery products from inventario_lotes
interface ProductoEntrega {
  nombre: string;
  codigo: string;
  cantidad: number;
}

const CalendarioEntregasTab = () => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [accionesDialogOpen, setAccionesDialogOpen] = useState(false);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState<any>(null);
  const [vistaCalendario, setVistaCalendario] = useState(true);
  const [mesActual, setMesActual] = useState(new Date());
  const [diaSeleccionado, setDiaSeleccionado] = useState<Date | null>(null);
  const [dialogDiaOpen, setDialogDiaOpen] = useState(false);
  const [filterProveedor, setFilterProveedor] = useState("todos");
  const [recepcionDetalleId, setRecepcionDetalleId] = useState<string | null>(null);
  const [recepcionDialogOpen, setRecepcionDialogOpen] = useState(false);
  
  // State for per-delivery products cache
  const [productosEntregaMap, setProductosEntregaMap] = useState<Record<string, ProductoEntrega[]>>({});
  const [loadingProductos, setLoadingProductos] = useState<Set<string>>(new Set());

  // Realtime subscription para sincronizar calendario de entregas
  useEffect(() => {
    const channel = supabase
      .channel('calendario-entregas-sync')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ordenes_compra_entregas'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["entregas_programadas_calendario"] });
          queryClient.invalidateQueries({ queryKey: ["ordenes_calendario_simples"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Fetch ALL scheduled deliveries from ordenes_compra_entregas
  // This now includes both multi-delivery OCs AND faltante follow-ups
  const { data: entregasProgramadas = [] } = useQuery({
    queryKey: ["entregas_programadas_calendario"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ordenes_compra_entregas")
        .select(
          `
          *,
          productos_faltantes,
          ordenes_compra!inner (
            id,
            folio,
            total,
            subtotal,
            impuestos,
            status,
            proveedor_id,
            proveedor_nombre_manual,
            proveedor_email_manual,
            tipo_pago,
            status_pago,
            entregas_multiples,
            fecha_entrega_programada,
            creado_por,
            autorizado_por,
            notas,
            proveedores (id, nombre, email, rfc),
            ordenes_compra_detalles (
              id,
              cantidad_ordenada,
              cantidad_recibida,
              precio_unitario_compra,
              subtotal,
              productos (id, codigo, nombre)
            )
          )
        `
        )
        // Exclude deliveries for OCs with pending advance payment
        .neq("ordenes_compra.status", "pendiente_pago")
        .order("fecha_programada");

      if (error) throw error;
      return data;
    },
  });


  // Fetch OCs that have NO records in ordenes_compra_entregas (legacy orders without delivery tracking)
  // These are older orders that only have fecha_entrega_programada on the OC itself
  const { data: ordenesSimples = [] } = useQuery({
    queryKey: ["ordenes_calendario_simples", entregasProgramadas],
    queryFn: async () => {
      // Get all OC IDs that already have delivery records
      const ocIdsWithDeliveries = new Set(
        entregasProgramadas.map((e: any) => e.ordenes_compra?.id).filter(Boolean)
      );

      const { data, error } = await supabase
        .from("ordenes_compra")
        .select(
          `
          *,
          proveedores (id, nombre, email, rfc),
          ordenes_compra_detalles (
            id,
            cantidad_ordenada,
            cantidad_recibida,
            precio_unitario_compra,
            subtotal,
            productos (id, codigo, nombre)
          )
        `
        )
        .not("fecha_entrega_programada", "is", null)
        .not("status", "eq", "cancelada")
        // Exclude OCs with pending advance payment
        .neq("status", "pendiente_pago")
        .order("fecha_entrega_programada");

      if (error) throw error;
      
      // Filter out OCs that already have entries in ordenes_compra_entregas
      return (data || []).filter((oc: any) => !ocIdsWithDeliveries.has(oc.id));
    },
    enabled: entregasProgramadas !== undefined,
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      programada: "secondary",
      pendiente: "secondary",
      parcial: "default",
      recibida: "default",
      entregada: "default",
      devuelta: "destructive",
    };
    return colors[status] || "secondary";
  };

  // Check if delivery was auto-rescheduled
  const esReprogramada = (notas: string | null) => {
    return notas?.includes("[AUTO]") || false;
  };

  // Helper to determine payment status: 'anticipado_pagado' | 'anticipado_pendiente' | 'contra_entrega'
  const getEstadoPago = (orden: any): 'anticipado_pagado' | 'anticipado_pendiente' | 'contra_entrega' => {
    if (orden?.tipo_pago === 'anticipado') {
      return orden?.status_pago === 'pagado' ? 'anticipado_pagado' : 'anticipado_pendiente';
    }
    return 'contra_entrega';
  };

  // Function to load specific products for a delivery from inventario_lotes
  const cargarProductosEntrega = useCallback(async (
    entregaId: string, 
    ocId: string, 
    folio: string, 
    numeroEntrega: number
  ) => {
    // Already in cache?
    if (productosEntregaMap[entregaId]) return;
    
    // Already loading?
    if (loadingProductos.has(entregaId)) return;
    
    setLoadingProductos(prev => new Set(prev).add(entregaId));
    
    try {
      const patronLote = `REC-${folio}-${numeroEntrega}`;
      const { data: lotes } = await supabase
        .from("inventario_lotes")
        .select(`
          cantidad_disponible,
          cantidad_inicial,
          producto:productos(nombre, codigo)
        `)
        .eq("orden_compra_id", ocId)
        .like("lote_referencia", `${patronLote}%`);

      if (lotes && lotes.length > 0) {
        setProductosEntregaMap(prev => ({
          ...prev,
          [entregaId]: lotes.map((l: any) => ({
            nombre: l.producto?.nombre || 'Producto',
            codigo: l.producto?.codigo || '',
            // Use cantidad_inicial as it represents what was received, not current available
            cantidad: l.cantidad_inicial || l.cantidad_disponible || 0
          }))
        }));
      }
    } catch (error) {
      console.error("Error loading delivery products:", error);
    } finally {
      setLoadingProductos(prev => {
        const next = new Set(prev);
        next.delete(entregaId);
        return next;
      });
    }
  }, [productosEntregaMap, loadingProductos]);

  // Combine both data sources into unified format
  const todasLasEntregas = useMemo(() => [
    // All delivery entries from ordenes_compra_entregas (including faltantes)
    ...entregasProgramadas.map((entrega: any) => {
      // Use fecha_entrega_real if completed, otherwise fecha_programada
      const esRecibida = entrega.status === 'recibida';
      const fechaMostrar = (esRecibida && entrega.fecha_entrega_real) 
        ? entrega.fecha_entrega_real 
        : entrega.fecha_programada;
      
      return {
        id: entrega.id,
        fecha: fechaMostrar,
        folio: entrega.ordenes_compra?.folio,
        proveedor: entrega.ordenes_compra?.proveedor_id 
          ? entrega.ordenes_compra?.proveedores?.nombre 
          : entrega.ordenes_compra?.proveedor_nombre_manual,
        esProveedorManual: !entrega.ordenes_compra?.proveedor_id,
        productos: entrega.ordenes_compra?.ordenes_compra_detalles,
        total: entrega.ordenes_compra?.total,
        status: entrega.status,
        orden: entrega.ordenes_compra,
        numeroEntrega: entrega.numero_entrega,
        cantidadBultos: entrega.cantidad_bultos,
        esMultiple: entrega.ordenes_compra?.entregas_multiples || entrega.origen_faltante,
        esFaltante: entrega.origen_faltante === true,
        productosFaltantes: entrega.productos_faltantes as Array<{
          producto_id: string;
          codigo: string;
          nombre: string;
          cantidad_faltante: number;
        }> | null,
        reprogramada: esReprogramada(entrega.notas),
        estadoPago: getEstadoPago(entrega.ordenes_compra),
        esCompletada: entrega.status === 'recibida',
      };
    }),
    // Legacy OCs without delivery tracking entries
    ...ordenesSimples.map((orden: any) => ({
      id: orden.id,
      fecha: orden.fecha_entrega_programada,
      folio: orden.folio,
      proveedor: orden.proveedor_id 
        ? orden.proveedores?.nombre 
        : orden.proveedor_nombre_manual,
      esProveedorManual: !orden.proveedor_id,
      productos: orden.ordenes_compra_detalles,
      total: orden.total,
      status: orden.status,
      orden: orden,
      numeroEntrega: null,
      cantidadBultos: null,
      esMultiple: false,
      esFaltante: false,
      productosFaltantes: null,
      reprogramada: esReprogramada(orden.notas),
      estadoPago: getEstadoPago(orden),
      esCompletada: orden.status === 'completada' || orden.status === 'recibida',
    })),
  ], [entregasProgramadas, ordenesSimples]);

  // Reset filter when month changes
  useEffect(() => { setFilterProveedor("todos"); }, [mesActual]);

  // Proveedores únicos del mes actual
  const proveedoresDelMes = useMemo(() => {
    const nombres = todasLasEntregas
      .filter(e => e.fecha && isSameMonth(new Date(e.fecha), mesActual))
      .map(e => e.proveedor)
      .filter(Boolean);
    return [...new Set(nombres)] as string[];
  }, [todasLasEntregas, mesActual]);

  // Entregas filtradas por proveedor
  const entregasFiltradas = useMemo(() => {
    if (filterProveedor === "todos") return todasLasEntregas;
    return todasLasEntregas.filter(e => e.proveedor === filterProveedor);
  }, [todasLasEntregas, filterProveedor]);

  // KPIs del mes actual (con filtro aplicado)
  const kpisMes = useMemo(() => {
    const delMes = entregasFiltradas.filter(e => e.fecha && isSameMonth(new Date(e.fecha), mesActual));
    return {
      pendientes: delMes.filter(e => e.status === "programada" || e.status === "pendiente").length,
      completadas: delMes.filter(e => e.esCompletada).length,
      faltantes: delMes.filter(e => e.esFaltante).length,
      totalEsperado: delMes.filter(e => !e.esCompletada).reduce((sum, e) => sum + (e.total || 0), 0),
    };
  }, [entregasFiltradas, mesActual]);

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(v);

  // Pre-load products for completed deliveries when switching to list view
  useEffect(() => {
    const cargarProductosEntregasRecibidas = async () => {
      const entregasRecibidas = todasLasEntregas.filter(
        e => e.esCompletada && e.numeroEntrega && e.orden
      );
      
      // Load first 20 to avoid too many requests
      for (const entrega of entregasRecibidas.slice(0, 20)) {
        if (!productosEntregaMap[entrega.id] && entrega.orden) {
          await cargarProductosEntrega(
            entrega.id,
            entrega.orden.id,
            entrega.folio,
            entrega.numeroEntrega
          );
        }
      }
    };
    
    if (!vistaCalendario && todasLasEntregas.length > 0) {
      cargarProductosEntregasRecibidas();
    }
  }, [vistaCalendario, todasLasEntregas.length]);

  // Helper to parse date string without timezone issues
  const parseDateLocal = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Map of dates with deliveries (uses filtered data)
  const entregasPorFecha = useMemo(() => {
    const mapa: Record<string, typeof entregasFiltradas> = {};
    entregasFiltradas.forEach((entrega) => {
      if (entrega.fecha) {
        // Use the date string directly as key to avoid timezone issues
        const fechaKey = entrega.fecha.split('T')[0]; // Handle both "2025-12-01" and "2025-12-01T00:00:00"
        if (!mapa[fechaKey]) {
          mapa[fechaKey] = [];
        }
        mapa[fechaKey].push(entrega);
      }
    });
    return mapa;
  }, [entregasFiltradas]);

  const agruparPorFecha = () => {
    const grupos: Record<string, typeof entregasFiltradas> = {};
    entregasFiltradas.forEach((entrega) => {
      if (entrega.fecha) {
        // Parse date without timezone conversion
        const dateStr = entrega.fecha.split('T')[0];
        const [year, month, day] = dateStr.split('-').map(Number);
        const fechaLocal = new Date(year, month - 1, day);
        const fecha = format(fechaLocal, "dd/MM/yyyy");
        if (!grupos[fecha]) {
          grupos[fecha] = [];
        }
        grupos[fecha].push(entrega);
      }
    });
    return grupos;
  };

  const gruposPorFecha = agruparPorFecha();

  // Calendar helpers
  const diasDelMes = useMemo(() => {
    const inicio = startOfWeek(startOfMonth(mesActual), { locale: es });
    const fin = endOfWeek(endOfMonth(mesActual), { locale: es });
    return eachDayOfInterval({ start: inicio, end: fin });
  }, [mesActual]);

  // Spanish locale starts week on Monday, so headers must match: Lunes, Martes, Miércoles, Jueves, Viernes, Sábado, Domingo
  const diasSemana = ["L", "M", "M", "J", "V", "S", "D"];

  const getEntregasDelDia = (dia: Date) => {
    const key = format(dia, "yyyy-MM-dd");
    return entregasPorFecha[key] || [];
  };

  const handleDiaClick = async (dia: Date) => {
    const entregas = getEntregasDelDia(dia);
    if (entregas.length > 0) {
      setDiaSeleccionado(dia);
      setDialogDiaOpen(true);
      
      // Load specific products for completed deliveries
      for (const entrega of entregas) {
        if (entrega.esCompletada && entrega.orden && entrega.numeroEntrega) {
          cargarProductosEntrega(
            entrega.id, 
            entrega.orden.id, 
            entrega.folio, 
            entrega.numeroEntrega
          );
        }
      }
    }
  };

  const entregasDelDiaSeleccionado = diaSeleccionado ? getEntregasDelDia(diaSeleccionado) : [];

  // Helper to render products for a delivery
  const renderProductosEntrega = (entrega: any) => {
    const productosRecibidos = productosEntregaMap[entrega.id];
    const isLoading = loadingProductos.has(entrega.id);
    
    // Case 1: Completed delivery with specific products loaded from inventario_lotes
    if (entrega.esCompletada && productosRecibidos && productosRecibidos.length > 0) {
      const productosOC = entrega.productos || [];
      const esEntregaParcial = productosRecibidos.length < productosOC.length;
      
      return (
        <div className="space-y-2">
          {esEntregaParcial && (
            <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800">
              Parcial ({productosRecibidos.length}/{productosOC.length} productos)
            </Badge>
          )}
          <div className="space-y-1">
            <p className="text-sm font-medium text-green-700 dark:text-green-400 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Productos recibidos en esta entrega:
            </p>
            <ul className="text-sm ml-4 list-disc text-muted-foreground">
              {productosRecibidos.map((p, idx) => (
                <li key={idx}>
                  <span className="font-medium">{p.cantidad}</span> {p.nombre}
                  {p.codigo && <span className="text-xs text-muted-foreground ml-1">({p.codigo})</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }
    
    // Case 2: Loading products
    if (entrega.esCompletada && isLoading) {
      return (
        <p className="text-sm text-muted-foreground animate-pulse">
          Cargando productos...
        </p>
      );
    }
    
    // Case 3: Faltante delivery - show from productos_faltantes
    if (entrega.esFaltante && entrega.productosFaltantes && entrega.productosFaltantes.length > 0) {
      return (
        <Alert className="mt-2 bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-800">
          <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
          <AlertDescription className="text-orange-700 dark:text-orange-300">
            <span className="font-medium">Productos de esta entrega (faltantes):</span>
            <ul className="mt-1 ml-4 list-disc">
              {entrega.productosFaltantes.map((p: any, idx: number) => (
                <li key={idx}>
                  <span className="font-medium">{p.cantidad_faltante}</span> {p.nombre}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      );
    }
    
    // Case 4: Pending/scheduled delivery - show OC products
    return (
      <p className="text-sm text-muted-foreground">
        {entrega.productos?.slice(0, 3).map((d: any, idx: number) => (
          <span key={idx}>
            {idx > 0 && ", "}
            <span className="font-medium">{d.cantidad_ordenada}</span> {d.productos?.nombre}
          </span>
        ))}
        {entrega.productos && entrega.productos.length > 3 && (
          <span> +{entrega.productos.length - 3} más</span>
        )}
      </p>
    );
  };

  // Helper to render products in list view (more compact)
  const renderProductosLista = (entrega: any) => {
    const productosRecibidos = productosEntregaMap[entrega.id];
    
    // Completed delivery with specific products
    if (entrega.esCompletada && productosRecibidos && productosRecibidos.length > 0) {
      return (
        <div className="text-sm">
          {productosRecibidos.slice(0, 2).map((p, idx) => (
            <span key={idx}>
              {idx > 0 && ", "}
              <span className="font-medium text-green-700 dark:text-green-400">{p.cantidad}</span>{" "}
              {p.nombre}
            </span>
          ))}
          {productosRecibidos.length > 2 && (
            <span className="text-muted-foreground"> +{productosRecibidos.length - 2} más</span>
          )}
        </div>
      );
    }
    
    // Faltante delivery
    if (entrega.esFaltante && entrega.productosFaltantes && entrega.productosFaltantes.length > 0) {
      return (
        <div className="text-sm">
          {entrega.productosFaltantes.slice(0, 2).map((p: any, idx: number) => (
            <span key={idx}>
              {idx > 0 && ", "}
              <span className="font-medium text-orange-600 dark:text-orange-400">{p.cantidad_faltante}</span>{" "}
              {p.nombre}
            </span>
          ))}
          {entrega.productosFaltantes.length > 2 && (
            <span className="text-muted-foreground"> +{entrega.productosFaltantes.length - 2} más</span>
          )}
        </div>
      );
    }
    
    // Default: show OC products
    return (
      <div className="text-sm">
        {entrega.productos?.slice(0, 2).map((d: any, idx: number) => (
          <span key={idx}>
            {idx > 0 && ", "}
            <span className="font-medium">{d.cantidad_ordenada}</span> {d.productos?.nombre}
          </span>
        ))}
        {entrega.productos && entrega.productos.length > 2 && (
          <span className="text-muted-foreground"> +{entrega.productos.length - 2} más</span>
        )}
      </div>
    );
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 sm:gap-3">
            <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            <h2 className="text-xl sm:text-2xl font-bold">Calendario de Entregas</h2>
            <LiveIndicator />
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant={vistaCalendario ? "default" : "outline"}
              size="sm"
              onClick={() => setVistaCalendario(true)}
              className="flex-1 sm:flex-initial"
            >
              <CalendarIcon className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Calendario</span>
              <span className="sm:hidden">Cal</span>
            </Button>
            <Button
              variant={!vistaCalendario ? "default" : "outline"}
              size="sm"
              onClick={() => setVistaCalendario(false)}
              className="flex-1 sm:flex-initial"
            >
              <List className="h-4 w-4 mr-1" />
              Lista
            </Button>
          </div>
        </div>
        <p className="text-muted-foreground text-sm">
          Visualiza y gestiona las entregas programadas de tus proveedores
        </p>
      </div>

      {/* KPIs del mes */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="p-3 rounded-lg border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="text-xs text-amber-700 dark:text-amber-400">Pendientes</span>
          </div>
          <p className="text-2xl font-bold text-amber-800 dark:text-amber-300">{kpisMes.pendientes}</p>
        </div>
        <div className="p-3 rounded-lg border bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-xs text-green-700 dark:text-green-400">Completadas</span>
          </div>
          <p className="text-2xl font-bold text-green-800 dark:text-green-300">{kpisMes.completadas}</p>
        </div>
        <div className="p-3 rounded-lg border bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800">
          <div className="flex items-center gap-2 mb-1">
            <PackageX className="h-4 w-4 text-orange-600" />
            <span className="text-xs text-orange-700 dark:text-orange-400">Faltantes</span>
          </div>
          <p className="text-2xl font-bold text-orange-800 dark:text-orange-300">{kpisMes.faltantes}</p>
        </div>
        <div className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-blue-600" />
            <span className="text-xs text-blue-700 dark:text-blue-400">$ Esperado</span>
          </div>
          <p className="text-2xl font-bold text-blue-800 dark:text-blue-300">{formatCurrency(kpisMes.totalEsperado)}</p>
        </div>
      </div>

      {/* Filtro por proveedor */}
      {proveedoresDelMes.length > 1 && (
        <div className="flex items-center gap-3 mb-4">
          <Select value={filterProveedor} onValueChange={setFilterProveedor}>
            <SelectTrigger className="w-[220px] h-9 text-xs">
              <SelectValue placeholder="Todos los proveedores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los proveedores</SelectItem>
              {proveedoresDelMes.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filterProveedor !== "todos" && (
            <span className="text-xs text-muted-foreground">
              Filtrando: <strong>{filterProveedor}</strong>
            </span>
          )}
        </div>
      )}

      {vistaCalendario ? (
        isMobile ? (
          /* ====== MOBILE: Vista semanal ====== */
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => { const d = new Date(mesActual); d.setDate(d.getDate() - 7); setMesActual(d); }}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
              </Button>
              <span className="text-sm font-medium">
                {format(mesActual, "dd MMM", { locale: es })} — {format(new Date(mesActual.getTime() + 6 * 86400000), "dd MMM", { locale: es })}
              </span>
              <Button variant="ghost" size="sm" onClick={() => { const d = new Date(mesActual); d.setDate(d.getDate() + 7); setMesActual(d); }}>
                Siguiente <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            {Array.from({ length: 7 }, (_, i) => {
              const dia = new Date(mesActual);
              dia.setDate(dia.getDate() - dia.getDay() + i + 1);
              const fechaKey = format(dia, "yyyy-MM-dd");
              const entregasDelDia = entregasPorFecha[fechaKey] || [];
              const esHoy = isSameDay(dia, new Date());
              const holiday = isHoliday(fechaKey);
              return (
                <div key={fechaKey} className={cn(
                  "border rounded-lg p-3",
                  esHoy && "border-primary bg-primary/5",
                  holiday && "border-red-300 bg-red-50/50 dark:bg-red-950/20",
                  entregasDelDia.length === 0 && !esHoy && !holiday && "opacity-50"
                )}>
                  <div className="flex justify-between items-center mb-1">
                    <span className={cn("text-sm font-medium capitalize", esHoy && "text-primary")}>
                      {format(dia, "EEEE dd", { locale: es })}
                    </span>
                    <div className="flex items-center gap-2">
                      {holiday && <Badge variant="outline" className="text-[10px] text-red-600 border-red-300">{holiday.shortName}</Badge>}
                      {entregasDelDia.length > 0 && <Badge variant="secondary" className="text-xs">{entregasDelDia.length}</Badge>}
                    </div>
                  </div>
                  {entregasDelDia.length > 0 ? entregasDelDia.map((e: any) => (
                    <div key={e.id} className="text-xs flex justify-between items-center py-1.5 border-t" onClick={() => { setDiaSeleccionado(dia); setDialogDiaOpen(true); }}>
                      <div className="flex items-center gap-2 min-w-0">
                        {e.esCompletada ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" /> :
                         e.esFaltante ? <PackageX className="h-3.5 w-3.5 text-orange-500 shrink-0" /> :
                         <Truck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                        <span className="truncate">{e.proveedor}</span>
                      </div>
                      <span className="text-muted-foreground shrink-0 ml-2">{e.cantidadBultos ? `${e.cantidadBultos} btos` : e.folio}</span>
                    </div>
                  )) : (
                    <p className="text-xs text-muted-foreground py-1">Sin entregas</p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
        /* ====== DESKTOP: Grid mensual ====== */
        <div className="space-y-4">
          {/* Calendar header */}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="icon" onClick={() => setMesActual(subMonths(mesActual, 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h3 className="text-lg font-semibold capitalize">
              {format(mesActual, "MMMM yyyy", { locale: es })}
            </h3>
            <Button variant="outline" size="icon" onClick={() => setMesActual(addMonths(mesActual, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day headers */}
            {diasSemana.map((dia, i) => (
              <div key={i} className="text-center py-2 text-sm font-medium text-muted-foreground">
                {dia}
              </div>
            ))}

            {/* Day cells */}
            {diasDelMes.map((dia, i) => {
              const entregas = getEntregasDelDia(dia);
              const esHoy = isSameDay(dia, new Date());
              const esDelMes = isSameMonth(dia, mesActual);
              const tieneEntregas = entregas.length > 0;
              const dateKey = format(dia, "yyyy-MM-dd");
              const holiday = isHoliday(dateKey);
              const esDomingo = dia.getDay() === 0;

              return (
                <TooltipProvider key={i}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        onClick={() => handleDiaClick(dia)}
                        className={cn(
                          "min-h-[72px] p-2 text-center rounded-lg transition-colors relative",
                          esDelMes ? "bg-background" : "bg-muted/30 text-muted-foreground",
                          tieneEntregas && "cursor-pointer hover:bg-accent",
                          esHoy && "ring-2 ring-primary ring-offset-2",
                          // Holiday and Sunday styling
                          holiday && "bg-red-50 dark:bg-red-950/20",
                          esDomingo && !holiday && "bg-muted/50"
                        )}
                      >
                        <span className={cn(
                          "inline-flex items-center justify-center w-8 h-8 rounded-full text-sm",
                          esHoy && "bg-primary text-primary-foreground",
                          holiday && !esHoy && "text-red-600 dark:text-red-400 font-medium"
                        )}>
                          {format(dia, "d")}
                        </span>
                        
                        {/* Holiday indicator */}
                        {holiday && (
                          <div className="absolute top-1 right-1">
                            <Flag className="w-3 h-3 text-red-500 dark:text-red-400" />
                          </div>
                        )}
                        
                        {/* Holiday name badge */}
                        {holiday && (
                          <div className="mt-0.5">
                            <span className="text-[9px] text-red-600 dark:text-red-400 truncate block leading-tight">
                              {holiday.shortName}
                            </span>
                          </div>
                        )}
                        
                        {/* Dots indicator - checkmark for completed, colored dots for pending */}
                        {tieneEntregas && (
                          <div className="flex flex-col items-center gap-1 mt-1">
                            <div className="flex justify-center gap-1">
                              {entregas.slice(0, 3).map((entrega, idx) => (
                                entrega.esCompletada ? (
                                  <span
                                    key={idx}
                                    className="w-4 h-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"
                                  >
                                    <CheckCircle2 className="w-3 h-3 text-green-600 dark:text-green-400" />
                                  </span>
                                ) : entrega.esFaltante ? (
                                  <span
                                    key={idx}
                                    className="w-4 h-4 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center"
                                  >
                                    <PackageX className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                                  </span>
                                ) : entrega.reprogramada ? (
                                  <span
                                    key={idx}
                                    className="w-4 h-4 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center"
                                  >
                                    <RotateCcw className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />
                                  </span>
                                ) : (
                                  <span
                                    key={idx}
                                    className={cn(
                                      "w-2 h-2 rounded-full",
                                      entrega.estadoPago === 'anticipado_pagado' && "bg-green-500",
                                      entrega.estadoPago === 'anticipado_pendiente' && "bg-yellow-500",
                                      entrega.estadoPago === 'contra_entrega' && "bg-rose-500"
                                    )}
                                  />
                                )
                              ))}
                              {entregas.length > 3 && (
                                <span className="text-xs text-muted-foreground">+{entregas.length - 3}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </TooltipTrigger>
                    {holiday && (
                      <TooltipContent>
                        <p className="font-medium">{holiday.name}</p>
                        <p className="text-xs text-muted-foreground">Día festivo oficial</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 pt-4 text-sm text-muted-foreground flex-wrap">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-3 h-3 text-green-600 dark:text-green-400" />
              </span>
              <span>Recibida</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <PackageX className="w-3 h-3 text-orange-600 dark:text-orange-400" />
              </span>
              <span>Faltante</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <RotateCcw className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />
              </span>
              <span>Reprog.</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500" />
              <span>Anticipado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-yellow-500" />
              <span>Antic. pend.</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-rose-500" />
              <span>Contra entrega</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-red-50 dark:bg-red-950/20 flex items-center justify-center border border-red-200 dark:border-red-800">
                <Flag className="w-2.5 h-2.5 text-red-500 dark:text-red-400" />
              </span>
              <span>Día festivo</span>
            </div>
          </div>
        </div>
        )
      ) : (
        // Lista view (existing)
        <>
          {Object.keys(gruposPorFecha).length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No hay entregas programadas
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(gruposPorFecha).map(([fecha, entregas]) => (
                <div key={fecha} className="border rounded-lg overflow-hidden">
                  <div className="bg-muted px-4 py-3">
                    <h3 className="font-semibold capitalize">{fecha}</h3>
                    <p className="text-sm text-muted-foreground">
                      {entregas.length} {entregas.length === 1 ? "entrega" : "entregas"}
                    </p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Folio</TableHead>
                        <TableHead>Proveedor</TableHead>
                        <TableHead>Productos</TableHead>
                        <TableHead>Bultos</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entregas.map((entrega) => (
                        <TableRow 
                          key={entrega.id}
                          className={cn(entrega.esCompletada && "opacity-60 bg-muted/30")}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2 flex-wrap">
                              {entrega.folio}
                              {entrega.esCompletada && (
                                <Badge className="text-xs bg-green-100 text-green-700 border-green-300 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Recibida
                                </Badge>
                              )}
                              {entrega.esFaltante && (
                                <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-800">
                                  <PackageX className="h-3 w-3 mr-1" />
                                  Faltante
                                </Badge>
                              )}
                              {entrega.esMultiple && !entrega.esFaltante && (
                                <Badge variant="outline" className="text-xs">
                                  <Truck className="h-3 w-3 mr-1" />
                                  #{entrega.numeroEntrega}
                                </Badge>
                              )}
                              {!entrega.esCompletada && entrega.estadoPago === 'anticipado_pagado' && (
                                <Badge className="text-xs bg-green-100 text-green-700 border-green-300 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800">
                                  <Banknote className="h-3 w-3 mr-1" />
                                  Anticipado pagado
                                </Badge>
                              )}
                              {!entrega.esCompletada && entrega.estadoPago === 'anticipado_pendiente' && (
                                <Badge className="text-xs bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-950/30 dark:text-yellow-400 dark:border-yellow-800">
                                  <Banknote className="h-3 w-3 mr-1" />
                                  Anticipo pendiente
                                </Badge>
                              )}
                              {entrega.reprogramada && (
                                <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800">
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                  Reprogramada
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{entrega.proveedor}</TableCell>
                          <TableCell>
                            {renderProductosLista(entrega)}
                          </TableCell>
                          <TableCell>
                            {entrega.cantidadBultos ? (
                              <span className="font-medium">{entrega.cantidadBultos.toLocaleString()} bultos</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={getStatusColor(entrega.status) as "default" | "secondary" | "destructive" | "outline"}>
                              {entrega.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {entrega.status === "recibida" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setRecepcionDetalleId(entrega.id);
                                    setRecepcionDialogOpen(true);
                                  }}
                                  title="Ver detalle de recepción"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setOrdenSeleccionada(entrega.orden);
                                  setAccionesDialogOpen(true);
                                }}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Dialog for day details */}
      <Dialog open={dialogDiaOpen} onOpenChange={setDialogDiaOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {diaSeleccionado && format(diaSeleccionado, "EEEE d 'de' MMMM yyyy", { locale: es })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {entregasDelDiaSeleccionado.map((entrega) => (
              <div
                key={entrega.id}
                className={cn(
                  "p-4 border rounded-lg hover:bg-accent/50 transition-colors",
                  entrega.esCompletada && "opacity-70 bg-muted/30"
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{entrega.folio}</span>
                    {entrega.esCompletada && (
                      <Badge className="text-xs bg-green-100 text-green-700 border-green-300 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Recibida
                      </Badge>
                    )}
                    {entrega.esFaltante && (
                      <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-800">
                        <PackageX className="h-3 w-3 mr-1" />
                        Faltante
                      </Badge>
                    )}
                    {entrega.esMultiple && !entrega.esFaltante && (
                      <Badge variant="outline" className="text-xs">
                        <Truck className="h-3 w-3 mr-1" />
                        Entrega #{entrega.numeroEntrega}
                      </Badge>
                    )}
                    {!entrega.esCompletada && entrega.estadoPago === 'anticipado_pagado' && (
                      <Badge className="text-xs bg-green-100 text-green-700 border-green-300 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800">
                        <Banknote className="h-3 w-3 mr-1" />
                        Anticipado pagado
                      </Badge>
                    )}
                    {!entrega.esCompletada && entrega.estadoPago === 'anticipado_pendiente' && (
                      <Badge className="text-xs bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-950/30 dark:text-yellow-400 dark:border-yellow-800">
                        <Banknote className="h-3 w-3 mr-1" />
                        Anticipo pendiente
                      </Badge>
                    )}
                    {entrega.reprogramada && (
                      <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800">
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Reprogramada
                      </Badge>
                    )}
                  </div>
                  <Badge variant={getStatusColor(entrega.status) as "default" | "secondary" | "destructive" | "outline"}>
                    {entrega.status}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{entrega.proveedor}</p>
                
                {/* Render specific products for this delivery */}
                {renderProductosEntrega(entrega)}
                
                {entrega.cantidadBultos && (
                  <p className="text-sm font-medium mt-2">{entrega.cantidadBultos.toLocaleString()} bultos</p>
                )}
                
                <div className="flex gap-2 mt-3">
                  {entrega.status === "recibida" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRecepcionDetalleId(entrega.id);
                        setRecepcionDialogOpen(true);
                        setDialogDiaOpen(false);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Ver Recepción
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setOrdenSeleccionada(entrega.orden);
                      setAccionesDialogOpen(true);
                      setDialogDiaOpen(false);
                    }}
                  >
                    <MoreVertical className="h-4 w-4 mr-1" />
                    Acciones
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <OrdenAccionesDialog
        open={accionesDialogOpen}
        onOpenChange={setAccionesDialogOpen}
        orden={ordenSeleccionada}
      />

      <RecepcionDetalleDialog
        entregaId={recepcionDetalleId}
        open={recepcionDialogOpen}
        onOpenChange={setRecepcionDialogOpen}
      />
    </Card>
  );
};

export default CalendarioEntregasTab;
